import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputBase,
  CircularProgress,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  X,
  Search,
  Hospital,
  MapPin,
  CheckCircle2,
  Send,
  FileText,
} from "lucide-react";

const ReferralDialog = ({
  referralOpen,
  setReferralOpen,
  hospitals = [],
  patient,
}) => {
  const [search, setSearch] = useState("");
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [loading, setLoading] = useState(false);

  const filteredHospitals = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return hospitals;

    return hospitals.filter((hospital) => {
      const name = hospital?.name || "";
      const address = hospital?.address || "";

      return (
        name.toLowerCase().includes(value) ||
        address.toLowerCase().includes(value)
      );
    });
  }, [search, hospitals]);

  const latestConsultation = useMemo(() => {
    const list = patient?.consultations || [];
    return list.length ? list[list.length - 1] : null;
  }, [patient]);

  useEffect(() => {
    if (!referralOpen) {
      setSearch("");
      setSelectedHospital(null);
      setLoading(false);
    }
  }, [referralOpen]);

  const handleSendReferral = async () => {
    if (!selectedHospital || !patient?._id) return;

    try {
      setLoading(true);

      const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/referrals`,
        {
          patientId: patient._id,
          hospitalId: selectedHospital.id,
          diagnosis: latestConsultation?.workingDiagnosis || "Not specified",
          clinicalNotes: latestConsultation?.clientHistory || "Not specified",
          medications: [],
          doctorId: doctor?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setReferralOpen(false);
    } catch (err) {
      console.error("Referral error:", err);
      alert(err?.response?.data?.message || "Failed to send referral");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={referralOpen}
      onClose={() => setReferralOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2.5,
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.03em] text-slate-950">
              Refer patient
            </h2>

            <p className="mt-1 text-sm font-normal text-slate-500">
              Select a hospital for {patient?.name || "this patient"}.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setReferralOpen(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Patient / diagnosis summary */}
        <div className="mb-5 mt-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-600">
              <FileText size={18} />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Current diagnosis
              </p>

              <p className="mt-1 line-clamp-2 text-sm font-medium leading-6 text-slate-800">
                {latestConsultation?.workingDiagnosis || "Not specified"}
              </p>

              <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500">
                {latestConsultation?.clientHistory ||
                  patient?.chiefComplaint ||
                  "No clinical note available."}
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-5">
          <label className="mb-2 block text-xs font-medium text-slate-500">
            Search hospital
          </label>

          <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
            <Search size={16} className="text-slate-400" />

            <InputBase
              placeholder="Search by hospital name or address"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                fontSize: "14px",
                color: "#0f172a",
                "& input::placeholder": {
                  color: "#94a3b8",
                  opacity: 1,
                },
              }}
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-[360px] overflow-y-auto pr-1">
          {filteredHospitals.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Hospital size={20} className="text-slate-500" />
              </div>

              <h3 className="text-sm font-semibold text-slate-950">
                No hospitals found
              </h3>

              <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-500">
                Try changing your search term.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHospitals.map((hospital) => {
                const isSelected = selectedHospital?.id === hospital.id;

                return (
                  <button
                    key={hospital.id}
                    type="button"
                    onClick={() => setSelectedHospital(hospital)}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      isSelected
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                          isSelected
                            ? "bg-slate-900 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {isSelected ? (
                          <CheckCircle2 size={18} />
                        ) : (
                          <Hospital size={18} />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-950">
                          {hospital.name || "Unnamed hospital"}
                        </p>

                        <p className="mt-1 flex items-start gap-1 text-xs leading-5 text-slate-500">
                          <MapPin size={13} className="mt-0.5 shrink-0" />
                          <span className="line-clamp-2">
                            {hospital.address || "No address"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected */}
        {selectedHospital && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Selected hospital
            </p>

            <p className="mt-1 truncate text-sm font-semibold text-slate-950">
              {selectedHospital.name}
            </p>

            <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
              {selectedHospital.address || "No address"}
            </p>
          </div>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #e2e8f0",
          px: 3,
          py: 2,
        }}
      >
        <button
          type="button"
          onClick={() => setReferralOpen(false)}
          className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={!selectedHospital || loading}
          onClick={handleSendReferral}
          className={`inline-flex h-10 items-center gap-2 rounded-xl px-5 text-sm font-medium transition ${
            !selectedHospital || loading
              ? "cursor-not-allowed bg-slate-100 text-slate-400"
              : "bg-[#075E54] text-white hover:bg-[#064b43]"
          }`}
        >
          {loading ? (
            <>
              <CircularProgress size={14} color="inherit" />
              Sending
            </>
          ) : (
            <>
              <Send size={15} />
              Send referral
            </>
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ReferralDialog;